"use client";

import React from "react";
import { IamUser } from "@guberno/data-access";
import { AppContext } from "./AppContextProvider";
import { isTenantWideScope } from "@guberno/utils/config";

/**
 * Permission data derived from the server-rendered session (userData).
 *
 * DISPLAY DATA ONLY — the backend re-derives permissions live from the
 * database on every mutation and is the enforcement point; these structures
 * only decide what the UI offers (docs/plans/circle-feature-plan.md).
 * A tampered client copy can at most show/hide UI, never grant access.
 */
export interface PermissionData {
  /** `basePermissions` — tenant-wide roles only. */
  base: Set<string>;
  /** `allPermissions` — base ∪ every per-circle effective set (display only). */
  all: Set<string>;
  /** circleID → `circlePermissions`: the complete effective set in that circle (base already unioned in). */
  perCircle: Map<string, Set<string>>;
}

/**
 * Derive permission data from the session user. Pure — unit-testable without React.
 */
export const derivePermissionData = (userData: IamUser | null | undefined): PermissionData => {
  const base = new Set<string>(userData?.basePermissions ?? []);
  const all = new Set<string>(userData?.allPermissions ?? []);
  const perCircle = new Map<string, Set<string>>();
  for (const member of userData?.memberCircles ?? []) {
    if (member.circleID && !isTenantWideScope(member.circleID)) {
      perCircle.set(member.circleID, new Set<string>(member.circlePermissions ?? []));
    }
  }
  return { base, all, perCircle };
};

/**
 * The derived permission data for the current session user.
 * Fresh on full page loads: the (guberno) server layout runs the
 * GetCurrentUser query and provides `userData` as a prop. Circle-management
 * saves use a full document reload/navigation to rebuild this context;
 * router.refresh() alone preserves its client-side state.
 */
export const usePermissionData = (): PermissionData => {
  const { localAppState } = React.useContext(AppContext);
  return React.useMemo(() => derivePermissionData(localAppState.userData), [localAppState.userData]);
};

/**
 * "May I do `permission` in this context?"
 *
 * - `circleID` omitted/null/zero → tenant-wide context → `permission ∈ basePermissions`.
 * - A real `circleID` → `permission ∈ basePermissions` OR `permission ∈ circlePermissions(circleID)`.
 *
 * Not a member-administration gate: those operations require strict MyAdminCircles authority.
 *
 * The base part matters for circles the user is NOT a member of: tenant-wide
 * permissions apply everywhere, including all circles (the backend
 * `IsUserPermittedInCircle` checks tenant-wide first, then the circle role).
 * For member circles `circlePermissions` already includes base, so the OR is
 * a no-op there.
 */
/**
 * Pure twin of `useCan` for non-hook contexts (grid row callbacks, event
 * handlers). Pass the result of `usePermissionData()`.
 */
export const can = (data: PermissionData, permission: string, circleID?: string | null): boolean => {
  if (!circleID || isTenantWideScope(circleID)) {
    return data.base.has(permission);
  }
  return data.base.has(permission) || (data.perCircle.get(circleID)?.has(permission) ?? false);
};

export const useCan = (permission: string, circleID?: string | null): boolean => {
  const data = usePermissionData();
  return can(data, permission, circleID);
};

/**
 * "May I do `permission` anywhere?" — for context-free affordances only
 * (top-bar "New…" buttons, menu visibility). Display data: never use it to
 * predict whether a specific action will succeed; the server decides per context.
 */
export const useCanAnywhere = (permission: string): boolean => {
  const { all } = usePermissionData();
  return all.has(permission);
};
